import { getInstance } from '@portal/repositories/securitySdkConfig';
import { IFormData } from '@portal/pages/UserManager/CreateUserManager/CreateUserManager';
import { keepOnlyNumbers } from '@portal/utils/format';
import type { PendingPixKeyPortability } from '@portal/models/module';

const BASE_URL = '/user';
const BASE_URL_V2 = '/v2/user';
const BASE_URL_V3 = '/v3/user';

export type GetMeResponse = {
  user: models.User;
  pendingDocuments: models.PendingDocument[];
  userCards: bank.Card[];
  userContractTerms?: models.ContractTerms[];
  roles: string[];
  pendingPixKeyPortabilities?: PendingPixKeyPortability[];
  ispb?: string;
  cardEligibility: {
    enabled: boolean;
  };
};

const UserApi = {
  getMe: async () => {
    const instance = getInstance();
    const { data } = await instance.get<GetMeResponse>(`${BASE_URL}/me`);

    return data;
  },

  availablePermissions: async () => {
    const instance = getInstance();
    const { data } = await instance.get(`${BASE_URL_V2}/permissions`);

    return data;
  },

  getById: async (userId: string) => {
    const instance = getInstance();
    const { data } = await instance.get(`${BASE_URL_V2}/${userId}`);

    return data;
  },

  myOperators: async (
    limit: number,
    offset: number,
    search: string,
    roleGroupId: string
  ) => {
    const instance = getInstance();
    const { data } = await instance.get(`${BASE_URL}/operators`, {
      params: {
        limit,
        page: offset,
        search,
        roleGroupId,
      },
    });

    return data;
  },

  updateLastLogin: async () => {
    const instance = getInstance();
    const { data } = await instance.get(`${BASE_URL}/update-last-login`);

    return data;
  },

  getSmsCorebank: async () => {
    const instance = getInstance();
    const { data } = await instance.post(`${BASE_URL}/corebank`);

    return data;
  },

  getForVinculateCompany: async () => {
    const instance = getInstance();
    const { data } = await instance.post(`${BASE_URL}/admin-corebank`);

    return data;
  },

  sendSmsCorebank: async (token: string) => {
    const instance = getInstance();
    const { data } = await instance.post(`${BASE_URL}/confirm-corebank`, {
      token,
    });

    return data;
  },

  sendSmsForVinculateCompany: async (token: string) => {
    const instance = getInstance();
    const { data } = await instance.post(`${BASE_URL}/confirm-admin-corebank`, {
      token,
    });

    return data;
  },

  createOperators: async (dataForm: IFormData) => {
    const instance = getInstance();

    const { data } = await instance.post(`${BASE_URL_V2}/access`, {
      login: dataForm.login,
      password: dataForm.password,
      taxId: keepOnlyNumbers(dataForm.document),
      name: dataForm.name,
      cellphone: '55' + dataForm.phone,
      email: dataForm.email,
      groupId: dataForm.group,
      bornDate: dataForm.birthDate,
    });

    return data;
  },

  updatePermissions: async (permissions: string[], userId: string) => {
    const instance = getInstance();
    const { data } = await instance.put(
      `${BASE_URL_V2}/${userId}/permissions`,
      {
        permissions,
      }
    );

    return data;
  },
  createUser: async (body: models.CreateUser) => {
    const instance = getInstance();
    const { data } = await instance.post(`${BASE_URL_V2}`, body);

    return data;
  },
  updateUser: async (userId: string, params: models.UpdateUser) => {
    const instance = getInstance();
    const { data } = await instance.patch(
      `${BASE_URL_V2}/update-multi-access-user/${userId}`,
      params
    );

    return data;
  },
  deleteUser: async (userId: string) => {
    const instance = getInstance();
    const { data } = await instance.delete(`${BASE_URL_V2}/${userId}`);

    return data;
  },

  acceptTerms: async (params: models.ContractTerms[]) => {
    const instance = getInstance();

    const contracts = params.map((contract) => ({
      id: contract.id,
      type: contract.type,
    }));

    const { data } = await instance.put(`/user-contract-terms`, {
      contracts,
    });

    return data;
  },

  checkToken: async ({ taxIdentifier, login, token }) => {
    const instance = getInstance();
    const { data } = await instance.post(`${BASE_URL_V3}/check-token`, {
      taxIdentifier,
      login,
      token,
    });

    return data;
  },
};

export default UserApi;
